import { SummaryList } from 'nhsuk-react-components';
import { Link } from 'react-router-dom';
import {
  getPriorityRangeByRuleType, isCustomRule, IterationRuleWithAndClauses, getRulesWithCopyInserted,
} from 'utils/ruleUtils';
import './RulesTable.scss';
import { IterationRule, RuleType } from 'schemas/IterationRule.schema';
import { buildRulePathParam } from 'utils/navigationUtils';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import useReferenceData from 'hooks/useReferenceData';
import { RoutingPlans } from 'schemas/RoutingPlan.schema';
import { getCohortStatus } from 'utils/cohortUtils.ts';
import { capitaliseFirstChar } from 'utils/formattingUtils.ts';
import RuleAttribute from './RuleAttributes';
import RuleRoutingStatus from './RuleRoutingStatus';

interface RuleRowProps {
    rules: IterationRuleWithAndClauses[];
    iterationId: string;
    ruleType: RuleType;
    ruleLabel: string;
    canUpdate: boolean;
    canCreateCustomRules: boolean;
    handleRulesSave: (rules: IterationRule[], ruleType: RuleType) => void;
    offSet?: number;
    teamRoutingPlans?: RoutingPlans;
}

export default function RuleRow({
  rules,
  iterationId,
  ruleType,
  ruleLabel,
  canUpdate,
  canCreateCustomRules,
  handleRulesSave,
  offSet = 0,
  teamRoutingPlans,
}: RuleRowProps) {
  const { referenceData } = useReferenceData();

  const cohortsData = referenceData.cohorts;

  const { getCampaignPathFor } = useCampaignNavigate();

  if (!rules.length) {
    return (
      <p className="nhsuk-u-margin-top-3">Rules for this iteration will appear here.</p>
    );
  }

  const priorityRange = getPriorityRangeByRuleType(ruleType);

  const commitChanges = (rulesWithAndClauses: IterationRuleWithAndClauses[]) => {
    const rulesToCommit: IterationRule[] = [];

    rulesWithAndClauses.map((rule, index) => {
      rulesToCommit.push({
        ID: rule.ID,
        Type: rule.Type,
        CohortLabel: rule.CohortLabel,
        Name: rule.Name,
        Description: rule.Description,
        Operator: rule.Operator,
        Comparator: rule.Comparator,
        AttributeTarget: rule.AttributeTarget,
        AttributeLevel: rule.AttributeLevel,
        AttributeName: rule.AttributeName,
        CommsRouting: rule.CommsRouting,
        AndNumber: 0,
        Priority: (index * 10) + priorityRange.lowerBound,
        LibraryRuleMetadata: rule.LibraryRuleMetadata,
      });

      rule.AndClauses?.forEach((andClause, idx) => {
        rulesToCommit.push({
          ID: andClause.ID,
          Type: andClause.Type,
          CohortLabel: andClause.CohortLabel,
          Name: andClause.Name,
          Description: andClause.Description,
          Operator: andClause.Operator,
          Comparator: andClause.Comparator,
          AttributeTarget: andClause.AttributeTarget,
          AttributeLevel: andClause.AttributeLevel,
          AttributeName: andClause.AttributeName,
          CommsRouting: andClause.CommsRouting,
          AndNumber: idx + 1,
          Priority: (index * 10) + priorityRange.lowerBound,
          LibraryRuleMetadata: andClause.LibraryRuleMetadata,
        });
      });

      return {
        ...rule,
        AndClauses: rule.AndClauses?.map((andClause) => ({
          ...andClause,
          Priority: (index * 10) + priorityRange.lowerBound,
        })),
      };
    });

    handleRulesSave(rulesToCommit, ruleType);
  };

  const handleCreateCopy = (parentRule: IterationRuleWithAndClauses) => {
    const newRules = getRulesWithCopyInserted(rules, parentRule);

    commitChanges(newRules);
  };

  function handleMove(selectedRule: IterationRuleWithAndClauses, direction: 'UP' | 'DOWN') {
    const currentIndex = rules.findIndex((rule) => rule.Priority === selectedRule.Priority);

    if ((direction === 'UP' && currentIndex === 0) || (direction === 'DOWN' && currentIndex === rules.length)) {
      return;
    }
    const newIndex = direction === 'UP' ? currentIndex - 1 : currentIndex + 1;

    const newRules = rules.map((item, index) => {
      if (index === newIndex) return rules[currentIndex];
      if (index === currentIndex) return rules[newIndex];
      return item;
    });

    commitChanges(newRules);
  }

  const handleDelete = (selectedRule: IterationRule) => {
    const newRules = rules.filter((rule) => rule.Priority !== selectedRule.Priority);

    commitChanges(newRules);
  };

  const handleDeleteAndClause = (parentRule: IterationRuleWithAndClauses, andClauseToDelete: IterationRule) => {
    const newRules = rules.map((rule) => {
      if (rule.Priority === parentRule.Priority) {
        return {
          ...rule,
          AndClauses: rule.AndClauses?.filter((ac) => ac.AndNumber !== andClauseToDelete.AndNumber),
        };
      }
      return rule;
    });

    commitChanges(newRules);
  };

  const buildUpdateLink = (rule: IterationRuleWithAndClauses) => getCampaignPathFor(`iteration/${iterationId}/${buildRulePathParam(ruleType)}/${rule.ID}?edit=true`);

  const buildAndClauseUpdateLink = (parentRuleId: string | undefined, andNumber: number | undefined) => getCampaignPathFor(`iteration/${iterationId}/${buildRulePathParam(ruleType)}/${parentRuleId}/and-clause/${andNumber}`);

  const getRuleNameDisplay = (rule: IterationRuleWithAndClauses) => {
    if (rule.DisplayName) {
      return rule.DisplayName;
    }

    return rule.Name || 'Not set';
  };

  return (
    <>
      {rules.map((rule, idx) => {
        const cohortStatus = getCohortStatus(rule.CohortLabel as string, cohortsData);
        return (
          <SummaryList.Row key={rule.ID ?? `${rule.Name}-${rule.Priority}`}>
            <SummaryList.Value>
              <div className="rule-details">
                <div className="rule-priority nhsuk-u-secondary-text-colour">
                  {idx + 1 + offSet}
                </div>
                <div>
                  <div>
                    <b>
                      {getRuleNameDisplay(rule)}
                    </b>
                    <RuleAttribute rule={rule} />
                    {rule.CohortLabel && rule.CohortLabel !== 'None' && (
                    <span data-testid="rule-cohort-label">
                      Cohort =
                      {' '}
                      {rule.CohortLabel}
                      {cohortStatus === 'ok' ? '' : ` (${capitaliseFirstChar(cohortStatus)})`}
                    </span>
                    )}
                  </div>
                  {rule.AndClauses?.map((andClause) => (
                    <div key={`${andClause.Name}-${andClause.AndNumber}`}>
                      <div className="rule-and-clause nhsuk-u-margin-top-3">
                        <b className="nhsuk-u-margin-top-1">AND&nbsp;</b>
                        {canUpdate && (
                          <div>
                              {(canCreateCustomRules || !isCustomRule(andClause)) && (
                              <Link
                                to={buildAndClauseUpdateLink(andClause.ID, andClause.AndNumber)}
                                aria-label={`Edit AND clause ${andClause.AndNumber}`}
                                data-testid={`and-edit-${ruleType}-${andClause.AndNumber}`}
                                className="nhsuk-back-link__link nhsuk-u-margin-left-2"
                              >
                                Edit
                              </Link>
                              )}
                            <button
                              type="button"
                              aria-label={`Delete AND clause ${andClause.AndNumber}`}
                              data-testid={`and-delete-${ruleType}-${andClause.AndNumber}`}
                              className="nhsuk-back-link__link delete-button nhsuk-u-margin-left-2"
                              onClick={() => handleDeleteAndClause(rule, andClause)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                      <RuleAttribute
                        rule={andClause}
                        isAndClause
                      />
                      {andClause.CohortLabel && andClause.CohortLabel !== 'None' && (
                        <span data-testid="rule-cohort-label">
                          Cohort =
                          {' '}
                          {andClause.CohortLabel}
                          {cohortStatus === 'ok' ? '' : ` (${cohortStatus})`}
                        </span>
                      )}
                    </div>
                  ))}
                  {rule.Type === 'R' && (
                    <RuleRoutingStatus teamRoutingPlans={teamRoutingPlans} commsRouting={rule.CommsRouting} />
                  )}
                </div>
              </div>
            </SummaryList.Value>
            {canUpdate && (
              <SummaryList.Action>
                <div>
                  {(canCreateCustomRules || !isCustomRule(rule)) && (
                  <Link
                    to={buildUpdateLink(rule)}
                    aria-label={`Edit ${ruleLabel} ${idx + 1}`}
                    data-testid={`rule-edit-${ruleType}-${idx}`}
                    className="nhsuk-back-link__link link-button nhsuk-u-margin-bottom-2"
                  >
                    Edit
                  </Link>
                  )}
                </div>
                {idx !== 0 && (
                  <div>
                    <button
                      type="button"
                      aria-label={`Move ${ruleLabel} ${idx + 1} up`}
                      data-testid={`rule-move-up-${ruleType}-${idx}`}
                      className="nhsuk-back-link__link link-button nhsuk-u-margin-bottom-2"
                      onClick={(e) => {
                        handleMove(rule, 'UP');
                        (e.target as HTMLButtonElement).blur();
                      }}
                    >
                      Move&nbsp;up
                    </button>
                  </div>
                )}
                {idx !== rules.length - 1 && (
                  <div>
                    <button
                      type="button"
                      aria-label={`Move ${ruleLabel} ${idx + 1} down`}
                      data-testid={`rule-move-down-${ruleType}-${idx}`}
                      className="nhsuk-back-link__link link-button nhsuk-u-margin-bottom-2"
                      onClick={(e) => {
                        handleMove(rule, 'DOWN');
                        (e.target as HTMLButtonElement).blur();
                      }}
                    >
                      Move&nbsp;down
                    </button>
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    aria-label={`Create a copy of ${ruleLabel} ${idx + 1}`}
                    data-testid={`rule-create-copy-${ruleType}-${idx}`}
                    className="nhsuk-back-link__link link-button nhsuk-u-margin-bottom-2"
                    onClick={(e) => {
                      handleCreateCopy(rule);
                      (e.target as HTMLButtonElement).blur();
                    }}
                  >
                    Create a copy
                  </button>
                </div>

                <div>
                  <button
                    type="button"
                    aria-label={`Delete ${ruleLabel} ${idx + 1}`}
                    data-testid={`rule-delete-${ruleType}-${idx}`}
                    className="nhsuk-back-link__link link-button nhsuk-u-margin-bottom-2 delete-button"
                    id={`delete-rule-${rule.Name}-${idx}`}
                    onClick={(e) => {
                      handleDelete(rule);
                      (e.target as HTMLButtonElement).blur();
                    }}
                  >
                    Delete rule
                  </button>
                </div>
              </SummaryList.Action>
            )}
          </SummaryList.Row>
        );
      })}
    </>
  );
}
